import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowRight, Plane, BarChart3, Users, Map, FileText,
  CreditCard, BookOpen, Upload, Package, MessageSquare,
  CheckCircle2, DollarSign, Globe, Smartphone, Zap, Shield,
  Target, Rocket, TrendingUp
} from 'lucide-react';

function TripTrustPage() {
  const navigate = useNavigate();
  const sectionsRef = useRef([]);

  useEffect(() => {
    const observers = sectionsRef.current.map((section) => {
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        },
        { threshold: 0.1 }
      );
      if (section) observer.observe(section);
      return observer;
    });

    return () => observers.forEach((observer) => observer.disconnect());
  }, []);

  const services = [
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Smart Dashboard",
      description: "Get instant clarity with real-time analytics on leads, revenue, conversion rates, and average quote values. Receive alerts for check-ins, follow-ups, and priority tasks."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Lead Management",
      description: "Capture inquiries from multiple sources and manage them through a structured pipeline. Plan trips with built-in tools for day-wise itineraries, meal plans, and budget tracking."
    },
    {
      icon: <BookOpen className="w-8 h-8" />,
      title: "Customer Database",
      description: "Access a centralized database of all customer profiles. Use advanced search filters and organized views to quickly locate customer details, previous trips, or preferences."
    },
    {
      icon: <Map className="w-8 h-8" />,
      title: "Itinerary Builder",
      description: "Create professional, multi-day itineraries with location details, activities, and traveler information. Save drafts or publish beautifully formatted itineraries for clients."
    },
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Quote Generator",
      description: "Generate accurate, professional travel quotes in minutes with automatic calculations for markups, GST, and TCS. Track quote status from draft to confirmed."
    },
    {
      icon: <Plane className="w-8 h-8" />,
      title: "Booking System",
      description: "Manage every booking efficiently with unique booking IDs, connected quotes, and travel date tracking. Integrate with external booking APIs."
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: "Payment Tracking",
      description: "Track payments linked to bookings and customers with clear visibility. Use status filters and date ranges to understand outstanding amounts and completed payments."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Vendor Management",
      description: "Maintain a full vendor directory with ratings, contact details, payment terms, and category tags. Streamline vendor coordination for all trips."
    },
    {
      icon: <Upload className="w-8 h-8" />,
      title: "Document Center",
      description: "Upload, organize, and secure all your documents. OCR scanning extracts text automatically, and documents can be linked directly to bookings."
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Package Catalog",
      description: "Create and manage ready-made travel packages with pricing, duration, destinations, inclusions, images, and itineraries for faster conversions."
    },
    {
      icon: <MessageSquare className="w-8 h-8" />,
      title: "Feedback System",
      description: "Automate post-trip feedback collection with scheduled invitations. Track response rates and manage testimonials to enhance trust."
    }
  ];

  const approach = [
    {
      phase: "01",
      title: "Lead Capture",
      description: "Capture inquiries from multiple channels and organize them in a structured pipeline for efficient follow-up.",
      icon: <Users className="w-6 h-6" />
    },
    {
      phase: "02",
      title: "Trip Planning",
      description: "Build customized itineraries with day-wise activities, meal plans, hotel preferences, and comprehensive budget tracking.",
      icon: <Map className="w-6 h-6" />
    },
    {
      phase: "03",
      title: "Quote Generation",
      description: "Generate professional quotes with automatic markup, GST, and TCS calculations. Track status from draft to confirmed.",
      icon: <FileText className="w-6 h-6" />
    },
    {
      phase: "04",
      title: "Booking & Payment",
      description: "Manage bookings with unique IDs and integrated APIs. Track all payments with complete financial visibility.",
      icon: <CreditCard className="w-6 h-6" />
    },
    {
      phase: "05",
      title: "Post-Trip Feedback",
      description: "Automate feedback collection and manage testimonials to continuously improve service quality and build trust.",
      icon: <MessageSquare className="w-6 h-6" />
    }
  ];

  const strengths = [
    { icon: <BarChart3 />, text: "Real-time analytics & instant alerts for complete operational visibility" },
    { icon: <Zap />, text: "End-to-end travel workflow automation from lead to feedback" },
    { icon: <DollarSign />, text: "Multi-currency support (INR & USD) for international operations" },
    { icon: <Globe />, text: "API integrations for external systems and booking platforms" },
    { icon: <Smartphone />, text: "Mobile-responsive interface accessible anywhere, anytime" },
    { icon: <Shield />, text: "Advanced search & filtering tools for efficient data management" }
  ];

  const benefits = [
    {
      title: "For Small Agencies",
      description: "Streamline operations, reduce manual work, and focus on growing your customer base with automated workflows.",
      icon: <Users />
    },
    {
      title: "For Growing Brands",
      description: "Scale efficiently with organized data, real-time insights, and seamless team collaboration across all departments.",
      icon: <TrendingUp />
    },
    {
      title: "For Established Operators",
      description: "Optimize performance, maximize revenue, and deliver exceptional customer experiences with enterprise-grade features.",
      icon: <Rocket />
    }
  ];

  const [currentImageIndex, setCurrentImageIndex] = React.useState(0);

  const heroImages = [
    { url: 'https://images.unsplash.com/photo-1586866991393-43a5cfd717bd', bgSize: 'cover' },
    { url: '/images/triptrust/hero-2.jpg', bgSize: 'cover' },
    { url: '/images/triptrust/hero-3.png', bgSize: 'contain', bgRepeat: 'no-repeat' }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImageIndex((prevIndex) => (prevIndex + 1) % heroImages.length);
    }, 5000);

    return () => clearInterval(interval);
  }, []);

  const currentImage = heroImages[currentImageIndex];

  return (
    <div className="triptrust-page">
      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg" style={{
          backgroundImage: `linear-gradient(135deg, rgba(10, 126, 164, 0.95) 0%, rgba(6, 90, 117, 0.95) 100%), url(${currentImage.url})`,
          backgroundSize: `cover, ${currentImage.bgSize}`,
          backgroundRepeat: `no-repeat, ${currentImage.bgRepeat || 'no-repeat'}`,
          backgroundPosition: 'center',
          transition: 'background-image 1s ease-in-out, background-size 1s ease-in-out'
        }}></div>
        <div className="hero-layout">
          <div className="hero-content">
            <div className="hero-badge">
              <Plane className="w-4 h-4" />
              <span>Your Complete Travel CRM — Smarter, Faster, All-in-One</span>
            </div>
            <h1 className="hero-title">
              Transform the Way Your<br />Travel Agency Works
            </h1>
            <p className="hero-subtitle">
              MyToursForYou CRM brings every part of your travel business into one intelligent platform—from lead capture to post-trip feedback
            </p>
            <div className="hero-cta">
              <Button
                size="lg"
                className="cta-primary"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Get Started <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="cta-secondary"
                onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
              >
                Book a Demo
              </Button>
            </div>
            <div className="hero-stats">
              <div className="stat-item">
                <div className="stat-number">11</div>
                <div className="stat-label">Core Features</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">100%</div>
                <div className="stat-label">Automation</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">24/7</div>
                <div className="stat-label">Access</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="section" ref={(el) => (sectionsRef.current[0] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">About MyToursForYou CRM</Badge>
            <h2 className="section-title">Built for Travel Agencies of All Sizes</h2>
            <p className="section-description">
              An end-to-end travel business management platform designed to help agencies work smarter and scale faster
            </p>
          </div>

          <div className="about-grid">
            <Card className="about-card">
              <CardHeader>
                <Target className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Product Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  From lead capture to post-trip feedback, every workflow is seamlessly connected, automated, and optimized for performance.
                </p>
              </CardContent>
            </Card>

            <Card className="about-card">
              <CardHeader>
                <Rocket className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Why MyToursForYou CRM?</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  Stay in control of your travel operations with a unified system. Gain real-time visibility, eliminate manual tasks, and boost productivity.
                </p>
              </CardContent>
            </Card>

            <Card className="about-card">
              <CardHeader>
                <TrendingUp className="w-10 h-10 mb-4 card-icon" />
                <CardTitle>Built for Growth</CardTitle>
              </CardHeader>
              <CardContent>
                <p>
                  Whether you're a small agency or a growing travel brand, MyToursForYou CRM helps you operate smoothly, stay organized, and deliver exceptional experiences.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="section section-alt" ref={(el) => (sectionsRef.current[1] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Core Features</Badge>
            <h2 className="section-title">Comprehensive Travel Business Management</h2>
            <p className="section-description">
              Everything you need to manage your travel agency—all in one powerful platform
            </p>
          </div>

          <div className="services-grid">
            {services.map((service, index) => (
              <Card key={index} className="service-card">
                <CardHeader>
                  <div className="service-icon">{service.icon}</div>
                  <CardTitle className="service-title">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="service-description">{service.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="section" ref={(el) => (sectionsRef.current[2] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Our Approach</Badge>
            <h2 className="section-title">End-to-End Travel Workflow</h2>
            <p className="section-description">
              Seamless automation from initial inquiry to post-trip feedback
            </p>
          </div>

          <div className="approach-timeline">
            {approach.map((phase, index) => (
              <div key={index} className="approach-item">
                <div className="approach-number">{phase.phase}</div>
                <div className="approach-content">
                  <div className="approach-icon">{phase.icon}</div>
                  <h3 className="approach-title">{phase.title}</h3>
                  <p className="approach-description">{phase.description}</p>
                </div>
                {index < approach.length - 1 && <div className="approach-line"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Strengths Section */}
      <section className="section section-alt" ref={(el) => (sectionsRef.current[3] = el)}>
        <div className="container">
          <div className="section-header">
            <Badge variant="outline" className="section-badge">Key Capabilities</Badge>
            <h2 className="section-title">Powerful Features for Modern Travel Agencies</h2>
          </div>

          <div className="strengths-grid">
            {strengths.map((strength, index) => (
              <div key={index} className="strength-item">
                <div className="strength-icon">{strength.icon}</div>
                <p className="strength-text">{strength.text}</p>
              </div>
            ))}
          </div>

          <Separator className="my-16" />

          <div className="engagement-section">
            <h3 className="engagement-title">Perfect for Every Travel Business</h3>
            <div className="engagement-grid">
              {benefits.map((benefit, index) => (
                <Card key={index} className="engagement-card">
                  <CardHeader>
                    <div className="engagement-icon">{benefit.icon}</div>
                    <CardTitle className="engagement-card-title">{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="engagement-card-description">{benefit.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section cta-section" ref={(el) => (sectionsRef.current[4] = el)}>
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Start Growing Your Travel Business Today</h2>
            <p className="cta-description">
              Try the CRM free — no credit card required, or book a demo to see the platform in action
            </p>
            <Button
              size="lg"
              className="cta-primary"
              onClick={() => navigate('/contact')}
            >
              Contact Us Today <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

export default TripTrustPage;
